import React, { useState, useMemo } from 'react';
import {
    Grid, Box, Typography, Container, ThemeProvider, createTheme, Alert,
    Card, CardContent, CardMedia, Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScanMeHeader from "../../components/scanMe/HeaderScanMe";
import { useSelector } from 'react-redux';
import defaultIcon from "../../assets/Icons/Automated baggage tracking service.png";
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

const theme = createTheme({
    typography: {
        fontFamily: 'Poppins, sans-serif',
    }
});

function ServiceDetails() {
    const [selectedService, setSelectedService] = useState(null);
    const travellerData = useSelector(state => state?.scanme?.travellerData);
    const AddonDetailsData = useSelector(state => state?.scanme?.travellerData?.[0]?.addon_details[0]?.add_ons || []);
    const navigate = useNavigate();
    console.log("travellerData in ServiceDetails:", travellerData);
    console.log("AddonDetailsData in ServiceDetails:", AddonDetailsData);

    // Memoize and separate services by inclusion_type
    const serviceDetailsData = useMemo(() =>
        travellerData?.[0]?.travellers?.[0]?.plan?.plan_service_alloweds || [],
        [travellerData]
    );

    const preInclusionServices = serviceDetailsData.filter(
        item => item.inclusion_type === 'pre_inclusion'
    );
    const duringInclusionServices = serviceDetailsData.filter(
        item => item.inclusion_type === 'during_inclusion'
    );
    const postInclusionServices = serviceDetailsData.filter(
        item => item.inclusion_type === 'post_inclusion'
    );

    function importAll(r) {
        let images = {};
        r.keys().forEach((item) => {
            // Remove extension and lowercase for key
            const key = item.replace('./', '').replace(/\.(svg|png)$/, '').toLowerCase();
            images[key] = r(item);
        });
        return images;
    }

    // Import both SVG and PNG files
    const iconsMap = {
        ...importAll(require.context('../../assets/Icons', false, /\.svg$/)),
        ...importAll(require.context('../../assets/Icons', false, /\.png$/)),
    };

    function normalize(str) {
        if (!str) return '';
        return str
            .toLowerCase()
            .replace(/[^a-z0-9]/g, ' ') // Remove all non-alphanumeric chars
            .replace(/\b(requirement|details|service|services|about|of|the|and|for|to|in|on|with|by|from|at|as|is|are|was|were|be|been|has|have|had|will|would|can|could|should|may|might|must|shall)\b/g, '') // Remove common words
            .replace(/\s+/g, ' ') // Collapse whitespace
            .trim();
    }

    // Enhanced fuzzy image match
    function getIconForService(serviceTitle, iconsMap, defaultIcon) {
        if (!serviceTitle) return defaultIcon;
        const normalizedTitle = normalize(serviceTitle);

        // Try exact match
        if (iconsMap[normalizedTitle]) return iconsMap[normalizedTitle];

        // Try partial match: find icon key that is included in the title or vice versa
        const foundKey = Object.keys(iconsMap).find(key => {
            const normalizedKey = normalize(key);
            return (
                normalizedTitle.includes(normalizedKey) ||
                normalizedKey.includes(normalizedTitle)
            );
        });
        if (foundKey) return iconsMap[foundKey];

        // Fallback
        return defaultIcon;
    }

    const handleCardClick = (item) => {
        setSelectedService(item);
    };

    const handleCloseDetails = () => {
        setSelectedService(null);
    };

    // Responsive columns for checkerboard coloring
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const columns = isMobile ? 2 : 4;

    // Checkerboard color function
    const getCheckerBgColor = (index) => {
        const row = Math.floor(index / columns);
        const col = index % columns;
        return ((row + col) % 2 === 0) ? '#153b85' : '#e3003a';
    };

    if (!travellerData) {
        return (
            <ThemeProvider theme={theme}>
                <ScanMeHeader />
                <Container sx={{ py: 4, textAlign: 'center' }}>
                    <Typography>Loading traveller data...</Typography>
                </Container>
            </ThemeProvider>
        );
    }

    if (!selectedService && serviceDetailsData.length === 0) {
        return (
            <ThemeProvider theme={theme}>
                <ScanMeHeader />
                <Container sx={{ py: 4 }}>
                    <Alert severity="info" sx={{ justifyContent: 'center' }}>
                        No specific service details were found for this plan.
                    </Alert>
                </Container>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <ScanMeHeader />
            <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
                {!selectedService &&
                    <Grid item xs={6} container justifyContent="flex-start">
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() =>
                                navigate(`/scanme-landing/${travellerData[0].document_id}`, {
                                    state: { travellerData },
                                })}
                            sx={{ ml: 1, backgroundColor: '#fcc000', borderRadius: '20px', color: 'white', textTransform: 'none' }}
                        >
                            Dashboard
                        </Button>
                    </Grid>
                }
                {selectedService ? (
                    <Box>
                        <Grid item xs={6} container justifyContent="flex-start">
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleCloseDetails}
                                sx={{ ml: 3, backgroundColor: '#fcc000', borderRadius: '20px', color: 'white', textTransform: 'none', mb: 4 }}
                            >
                                Benefits
                            </Button>
                        </Grid>
                        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} sm={4} md={3}>
                                        <CardMedia
                                            component="img"
                                            sx={{ height: 180, width: '100%', objectFit: 'contain', borderRadius: '4px' }}
                                            image={getIconForService(selectedService.service?.title, iconsMap, defaultIcon)}
                                            alt={`${selectedService?.service?.title ?? 'Service'} illustration`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={9}>
                                        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            {selectedService?.service?.title ?? 'Service Details'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card sx={{ border: '1px solid', borderColor: 'divider', padding: 2 }}>
                            <CardContent>
                                <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Description:
                                </Typography>
                                <Typography >
                                    {(!selectedService?.service?.description || selectedService?.service?.description.toUpperCase() === 'NA')
                                        ? 'Description Not Available'
                                        : selectedService?.service?.description
                                    }
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                ) : (
                    <>
                        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: { xs: 2, sm: 4 }, fontWeight: 500 }}>
                            {travellerData?.[0]?.travellers?.[0]?.firstName}'s Premium Plan Benefits
                        </Typography>
                        {/* Pre-Inclusion Section */}
                        {preInclusionServices.length > 0 && (
                            <>
                                <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: '#00239c' }}>
                                    Pre-Trip Services
                                </Typography>
                                <Grid container spacing={{ xs: 2, sm: 0 }}>
                                    {preInclusionServices.map((item, index) => {
                                        const serviceTitle = item.service?.title;
                                        const imagePath = getIconForService(serviceTitle, iconsMap, defaultIcon);
                                        return (
                                            <Grid item xs={6} sm={4} md={3} key={item.id || index}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        color: 'white',
                                                        minHeight: { xs: 140, sm: 160 },
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        padding: { xs: '15px', sm: '20px' },
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: "110% center",
                                                        backgroundSize: '120px 100px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                        fontFamily: 'Poppins, sans-serif',
                                                        borderRadius: { xs: '8px', sm: 0 },
                                                        boxShadow: { xs: '0 4px 10px rgba(0, 0, 0, 0.1)', sm: 'none' },
                                                        height: '100%',
                                                        backgroundColor: getCheckerBgColor(index),
                                                        backgroundImage: `url("${imagePath}")`,
                                                        '&:hover': {
                                                            transform: 'translateY(-5px) scale(1.02)',
                                                            zIndex: 10,
                                                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                        }
                                                    }}
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            textShadow: '1px 1px darkgray',
                                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                                            WebkitTextStroke: '0.3px gray',
                                                            display: 'block',
                                                            lineHeight: 1.2,
                                                            mb: 1.5,
                                                            mt: 0.5,
                                                            textAlign: 'start',
                                                            alignSelf: 'flex-start',
                                                        }}
                                                    >
                                                        {item.service?.title ?? 'Details Unavailable'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        )}
                        {/* During-Inclusion Section */}
                        {duringInclusionServices.length > 0 && (
                            <>
                                <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#00239c' }}>
                                     During Trip Services
                                </Typography>
                                <Grid container spacing={{ xs: 2, sm: 0 }}>
                                    {duringInclusionServices.map((item, index) => {
                                        const serviceTitle = item.service?.title;
                                        const imagePath = getIconForService(serviceTitle, iconsMap, defaultIcon);
                                        return (
                                            <Grid item xs={6} sm={4} md={3} key={item.id || index}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        color: 'white',
                                                        minHeight: { xs: 140, sm: 160 },
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        padding: { xs: '15px', sm: '20px' },
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: "110% center",
                                                        backgroundSize: '120px 100px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                        fontFamily: 'Poppins, sans-serif',
                                                        borderRadius: { xs: '8px', sm: 0 },
                                                        boxShadow: { xs: '0 4px 10px rgba(0, 0, 0, 0.1)', sm: 'none' },
                                                        height: '100%',
                                                        backgroundColor: getCheckerBgColor(index),
                                                        backgroundImage: `url("${imagePath}")`,
                                                        '&:hover': {
                                                            transform: 'translateY(-5px) scale(1.02)',
                                                            zIndex: 10,
                                                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                        }
                                                    }}
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            textShadow: '1px 1px darkgray',
                                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                                            WebkitTextStroke: '0.3px gray',
                                                            display: 'block',
                                                            lineHeight: 1.2,
                                                            mb: 1.5,
                                                            mt: 0.5,
                                                            textAlign: 'start',
                                                            alignSelf: 'flex-start',
                                                        }}
                                                    >
                                                        {item.service?.title ?? 'Details Unavailable'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        )}
                        {/* Post-Inclusion Section */}
                        {postInclusionServices.length > 0 && (
                            <>
                                <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#00239c' }}>
                                    Post Trip Services
                                </Typography>
                                <Grid container spacing={{ xs: 2, sm: 0 }}>
                                    {postInclusionServices.map((item, index) => {
                                        const serviceTitle = item.service?.title;
                                        const imagePath = getIconForService(serviceTitle, iconsMap, defaultIcon);
                                        return (
                                            <Grid item xs={6} sm={4} md={3} key={item.id || index}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        color: 'white',
                                                        minHeight: { xs: 140, sm: 160 },
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        padding: { xs: '15px', sm: '20px' },
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: "110% center",
                                                        backgroundSize: '120px 100px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                        fontFamily: 'Poppins, sans-serif',
                                                        borderRadius: { xs: '8px', sm: 0 },
                                                        boxShadow: { xs: '0 4px 10px rgba(0, 0, 0, 0.1)', sm: 'none' },
                                                        height: '100%',
                                                        backgroundColor: getCheckerBgColor(index),
                                                        backgroundImage: `url("${imagePath}")`,
                                                        '&:hover': {
                                                            transform: 'translateY(-5px) scale(1.02)',
                                                            zIndex: 10,
                                                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                        }
                                                    }}
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            textShadow: '1px 1px darkgray',
                                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                                            WebkitTextStroke: '0.3px gray',
                                                            display: 'block',
                                                            lineHeight: 1.2,
                                                            mb: 1.5,
                                                            mt: 0.5,
                                                            textAlign: 'start',
                                                            alignSelf: 'flex-start',
                                                        }}
                                                    >
                                                        {item.service?.title ?? 'Details Unavailable'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        )}

                         {AddonDetailsData.length > 0 && (
                            <>
                                <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#00239c' }}>
                                    Assistance Add-Ons
                                </Typography>
                                <Grid container spacing={{ xs: 2, sm: 0 }}>
                                    {AddonDetailsData.map((item, index) => {
                                        const serviceTitle = item.service?.title;
                                        const imagePath = getIconForService(serviceTitle, iconsMap, defaultIcon);
                                        return (
                                            <Grid item xs={6} sm={4} md={3} key={item.id || index}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        color: 'white',
                                                        minHeight: { xs: 140, sm: 160 },
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        padding: { xs: '15px', sm: '20px' },
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: "110% center",
                                                        backgroundSize: '120px 100px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                        fontFamily: 'Poppins, sans-serif',
                                                        borderRadius: { xs: '8px', sm: 0 },
                                                        boxShadow: { xs: '0 4px 10px rgba(0, 0, 0, 0.1)', sm: 'none' },
                                                        height: '100%',
                                                        backgroundColor: getCheckerBgColor(index),
                                                        backgroundImage: `url("${imagePath}")`,
                                                        '&:hover': {
                                                            transform: 'translateY(-5px) scale(1.02)',
                                                            zIndex: 10,
                                                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                        }
                                                    }}
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            textShadow: '1px 1px darkgray',
                                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                                            WebkitTextStroke: '0.3px gray',
                                                            display: 'block',
                                                            lineHeight: 1.2,
                                                            mb: 1.5,
                                                            mt: 0.5,
                                                            textAlign: 'start',
                                                            alignSelf: 'flex-start',
                                                        }}
                                                    >
                                                        {item.service?.title ?? 'Details Unavailable'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Container>
        </ThemeProvider>
    );
}

export default ServiceDetails;